import { z } from 'zod';

import { LayoutOperationSchema } from './prepackaged-label-layout-operation';
import {
  PrepackagedChecklistEditorTargetSchema,
  PrepackagedLegalSourceSchema,
} from './prepackaged-label-regulatory';

export const PREPACKAGED_ASSISTANT_MESSAGE_ROLES = ['USER', 'ASSISTANT'] as const;
export type PrepackagedAssistantMessageRole = (typeof PREPACKAGED_ASSISTANT_MESSAGE_ROLES)[number];

/**
 * A suggestion either carries a value the user can write into the form with
 * one tap (APPLY_VALUE), points at the place where the user has to act himself
 * (OPEN_FIELD, e.g. the recipe ingredients, which are edited elsewhere), or
 * asks for one layout change on the editor canvas (LAYOUT_OPERATION — exported
 * but never emitted by the backend until the free-mode wave).
 */
export const PREPACKAGED_ASSISTANT_SUGGESTION_KINDS = [
  'APPLY_VALUE',
  'OPEN_FIELD',
  'LAYOUT_OPERATION',
] as const;
export type PrepackagedAssistantSuggestionKind =
  (typeof PREPACKAGED_ASSISTANT_SUGGESTION_KINDS)[number];

/** The value an APPLY_VALUE card writes: never null — null is OPEN_FIELD's "no value". */
const PresentSuggestionValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
]);

export const PrepackagedAssistantSuggestionValueSchema = z.union([
  PresentSuggestionValueSchema,
  z.null(),
]);
export type PrepackagedAssistantSuggestionValue = z.infer<
  typeof PrepackagedAssistantSuggestionValueSchema
>;

/** Fields every suggestion card carries, whichever kind it is. */
const suggestionBase = z.object({
  id: z.string().min(1),
  /** Human-readable field name, already resolved server-side. */
  fieldLabel: z.string().min(1),
  /** Why this value, in plain language. */
  rationale: z.string().min(1),
  legalSources: z.array(PrepackagedLegalSourceSchema),
});

export const PrepackagedAssistantSuggestionSchema = z.discriminatedUnion('kind', [
  suggestionBase.extend({
    kind: z.literal('APPLY_VALUE'),
    target: PrepackagedChecklistEditorTargetSchema,
    value: PresentSuggestionValueSchema,
    /** Human-readable value, already resolved server-side (never a raw enum token). */
    displayValue: z.string().min(1),
  }),
  suggestionBase.extend({
    kind: z.literal('OPEN_FIELD'),
    target: PrepackagedChecklistEditorTargetSchema,
    value: z.null(),
    displayValue: z.null(),
  }),
  suggestionBase.extend({
    kind: z.literal('LAYOUT_OPERATION'),
    operation: LayoutOperationSchema,
    displayValue: z.string().min(1),
  }),
]);
export type PrepackagedAssistantSuggestion = z.infer<typeof PrepackagedAssistantSuggestionSchema>;

/**
 * An unknown card kind (a newer backend talking to an older client, or a row
 * written by a future contract) is dropped one card at a time: the thread
 * stays readable instead of failing as a whole.
 */
export const PrepackagedAssistantSuggestionListSchema = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = PrepackagedAssistantSuggestionSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

/**
 * Compact, server-derived grounding metadata persisted beside an assistant
 * message: which regulatory rule the reply addressed and which knowledge-base
 * sections back it, with the legal sources deduplicated from those sections.
 * `null` on user/import rows and on messages stored before this field existed.
 */
export const PrepackagedAssistantGroundingSchema = z.object({
  addressedRuleId: z.string().min(1).nullable(),
  sectionIds: z.array(z.string().min(1)),
  legalSources: z.array(PrepackagedLegalSourceSchema),
});
export type PrepackagedAssistantGrounding = z.infer<typeof PrepackagedAssistantGroundingSchema>;

export const PrepackagedAssistantMessageSchema = z.object({
  id: z.string().min(1),
  role: z.enum(PREPACKAGED_ASSISTANT_MESSAGE_ROLES),
  text: z.string().min(1),
  // Tolerant list: an unknown card kind is dropped, the message stays readable.
  suggestions: PrepackagedAssistantSuggestionListSchema,
  // Default (non optional): payload precedenti al campo — fixture e messaggi
  // storici — restano validi e vengono letti come "senza grounding".
  grounding: PrepackagedAssistantGroundingSchema.nullable().default(null),
  createdAt: z.iso.datetime(),
});
export type PrepackagedAssistantMessage = z.infer<typeof PrepackagedAssistantMessageSchema>;

export const PrepackagedAssistantThreadSchema = z.object({
  recipeId: z.string().min(1),
  messages: z.array(PrepackagedAssistantMessageSchema),
});
export type PrepackagedAssistantThread = z.infer<typeof PrepackagedAssistantThreadSchema>;

/** `text: null` opens the conversation: the assistant greets and proposes the next step. */
export const PrepackagedAssistantSendRequestSchema = z.object({
  text: z.string().trim().min(1).max(2000).nullable(),
});
export type PrepackagedAssistantSendRequest = z.infer<typeof PrepackagedAssistantSendRequestSchema>;

export const PrepackagedAssistantSendResponseSchema = z.object({
  messages: z.array(PrepackagedAssistantMessageSchema),
});
export type PrepackagedAssistantSendResponse = z.infer<
  typeof PrepackagedAssistantSendResponseSchema
>;

export const PREPACKAGED_ASSISTANT_MESSAGE_LIMIT = 40;
export const PREPACKAGED_ASSISTANT_SUGGESTIONS_PER_MESSAGE = 3;

/**
 * Cap for the label-import turn: its suggestions are reviewed in a dedicated
 * dialog listing every proposed change, so the inline per-message cap does not
 * apply there.
 */
export const PREPACKAGED_ASSISTANT_IMPORT_SUGGESTIONS_MAX = 20;

/**
 * userText persisted when the import turn carries attachments and no text:
 * the thread requires a non-empty user row.
 */
export const PREPACKAGED_ASSISTANT_IMPORT_PLACEHOLDER = 'Etichetta allegata';
